import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, Monitor, Tv, Smartphone, Laptop, ShieldCheck, Zap, Settings, Globe, Info, Cpu, HardDrive, Smartphone as Tablet } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Quais Dispositivos São Compatíveis com IPTV?", href: "/blog/quais-dispositivos-compativeis-iptv" }
  ];

  const faq = [
    {
      question: "Qualquer Smart TV roda IPTV?",
      answer: "A maioria das Smart TVs modernas (Samsung, LG, Android TV) suporta aplicativos de IPTV. Modelos muito antigos (com mais de 8 anos) podem ter sistemas operacionais limitados ou lojas de apps fechadas."
    },
    {
      question: "O IPTV funciona no videogame (PS5/Xbox)?",
      answer: "Sim, através de players que rodam no navegador ou aplicativos de terceiros disponíveis nas lojas, embora a experiência seja inferior a um dispositivo dedicado como uma TV Box."
    },
    {
      question: "Posso usar IPTV no computador?",
      answer: "Com certeza. Existem programas excelentes para Windows, Mac e Linux que rodam listas IPTV com fluidez e permitem até gravar conteúdos."
    },
    {
      question: "Preciso de um aparelho caro para rodar 4K?",
      answer: "Não necessariamente caro, mas potente. Para 4K, você precisa de um processador de vídeo que suporte o codec H.265 (HEVC), presente em quase todos os dispositivos de elite de 2026."
    }
  ];

  const benefits = [
    {
      title: "Liberdade de Escolha",
      description: "Assista em qualquer tela que você já possui em casa, sem amarras.",
      icon: <Monitor className="w-6 h-6" />
    },
    {
      title: "Sincronia Multi-Device",
      description: "Comece a assistir na TV e termine no celular exatamente de onde parou.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Futuro Garantido",
      description: "Compatibilidade com os novos padrões de vídeo 8K e HDR10+.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Mapeamento de Sistema",
      description: "Identifique se o seu aparelho usa Android, iOS, Tizen ou WebOS."
    },
    {
      title: "Seleção de Player",
      description: "Escolha o aplicativo homologado pela P2 PRO para aquele hardware."
    },
    {
      title: "Configuração de Login",
      description: "Insira seus dados únicos para carregar o catálogo de canais."
    },
    {
      title: "Otimização de Vídeo",
      description: "Ajuste os codecs para extrair a máxima fidelidade visual do aparelho."
    }
  ];

  const diferenciais = [
    {
      title: "Agnostic Platform",
      description: "Nossa infraestrutura P2 PRO foi construída para ser compatível com 99% dos hardware mundiais.",
      icon: <Globe className="w-6 h-6" />
    },
    {
      title: "High Performance API",
      description: "Conexões otimizadas para processadores ARM e x86, garantindo trocas de canais ultra-rápidas.",
      icon: <Cpu className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: O Fim da Dependência de Receptores",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O tempo em que você precisava de um receptor específico ligado à antena para assistir TV acabou. Em 2026, o IPTV de elite da <strong>P2 PRO</strong> transforma praticamente qualquer tela conectada à internet em uma central de entretenimento 4K. A compatibilidade é a alma do nosso serviço. Se o seu dispositivo tem um processador de vídeo e acesso à web, as chances são de que ele possa rodar nossa grade completa.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia denso e técnico, vamos mapear o ecossistema de hardware compatível. Explicamos as nuances de cada sistema operacional, os limites de cada dispositivo e como escolher a tela certa para cada momento do seu dia.
          </p>
        </div>
      )
    },
    {
      title: "2. O Ecossistema de Telas Grandes (Sala e Quarto)",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Tv className="w-6 h-6 text-electric" /> Smart TVs Nativas</h4>
              <p className="text-sm text-muted-foreground mb-4">Samsung (Tizen), LG (WebOS) e Sony/Philips (Android TV) são as líderes. A vantagem é não precisar de cabos extras; o IPTV roda direto na interface da TV.</p>
              <div className="p-3 bg-electric/10 border border-electric/20 rounded-xl">
                <p className="text-xs text-electric"><strong>Dica:</strong> Em TVs, priorize aplicativos que usem decodificação por hardware para evitar lentidão nos menus.</p>
              </div>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Cpu className="w-6 h-6 text-amber-500" /> Dispositivos de Streaming</h4>
              <p className="text-sm text-muted-foreground mb-4">Fire TV Stick, Mi Box, Mi Stick e Apple TV. São a melhor opção para transformar TVs 'burras' ou Smart TVs lentas em máquinas de streaming ultra-velozes.</p>
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-xs text-amber-500"><strong>Dica:</strong> O Fire Stick 4K é atualmente o melhor custo-benefício para rodar a P2 PRO com perfeição.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Mobilidade e Computação (Trabalho e Viagem)",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic"><Smartphone /></div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Smartphones e Tablets (iOS/Android)</h4>
              <p className="text-muted-foreground">Tanto o iPhone canto celulares Samsung, Xiaomi ou Motorola são compatíveis. Nos Androids, você conta com os apps oficiais <strong>One X</strong> e <strong>P2 PLAY</strong>. No iOS, existem players de elite na App Store como o <em>IBO Player</em>.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic"><Laptop /></div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Computadores e Notebooks</h4>
              <p className="text-muted-foreground">Utilize o <strong>IPTV Smarters</strong> para Windows ou o <strong>VLC Player</strong> para Mac. A compatibilidade x86 permite rodar múltiplos canais ao mesmo tempo em diferentes janelas, ideal para monitoramento de câmeras ou eventos esportivos múltiplos.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dispositivos de Nicho e Consoles",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">A P2 PRO vai onde você for, mesmo em aparelhos que você não imagina:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Consoles de Videogame</h5>
                <p className="text-sm text-muted-foreground">O Xbox One e Series X/S possuem o app <em>IPTV Smarters</em> na loja oficial. No PlayStation, você pode assistir via navegador Web acessando nosso player exclusivo.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Projetores Inteligentes</h5>
                <p className="text-sm text-muted-foreground">Projetores com Android nativo (como os da Samsung ou chineses de boa qualidade) rodam nossos apps, permitindo criar um cinema de 120 polegadas com IPTV.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Atenção ao Roku</h5>
                <p className="text-sm text-muted-foreground">O sistema Roku é o mais fechado de todos. Embora compatível, ele exige métodos de instalação específicos que detalhamos em nosso guia dedicado ao Roku.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Quais Dispositivos São Compatíveis com IPTV? O Guia de Hardware 2026"
        subtitle="Mapeamento completo: de Smart TVs a videogames, saiba onde você pode rodar o sinal de elite da P2 PRO."
        heroImage="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "A versatilidade é o que torna o IPTV da P2 PRO superior a qualquer TV por assinatura tradicional. Você não está preso a um 'decodificador' proprietário que só funciona na sala. Em 2026, seu entretenimento é ubíquo: ele vive na sua TV, no seu bolso, no seu computador de trabalho e até no seu projetor de lazer.",
          "Neste guia denso, exploramos a vasta gama de dispositivos compatíveis. Detalhamos as exigências técnicas para cada tipo de hardware, as limitações de cada sistema operacional e como extrair a máxima performance de vídeo em cada uma dessas telas.",
          "Prepare-se para descobrir que você já tem em mãos tudo o que precisa para acessar o melhor servidor de IPTV do Brasil, em todos os cômodos da sua casa e além."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="📱 Quer Assistir em Múltiplos Aparelhos ao Mesmo Tempo?"
        ctaDescription="Conheça nossos planos multi-telas e leve sua assinatura P2 PRO para toda a família simultaneamente."
        ctaButtonText="Ver Planos Multi-Telas"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/quais-dispositivos-compativeis-iptv")({
  head: () => ({
    meta: [
      { title: "Quais Dispositivos São Compatíveis com IPTV? Guia 2026" },
      { name: "description", content: "Descubra a lista completa de aparelhos compatíveis com IPTV: Smart TVs, TV Boxes, Fire Stick, Xbox, Celulares e muito mais. Saiba onde rodar a P2 PRO." },
    ],
  }),
  component: ArticlePage,
});
